/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setLanguage } from "../../actions/admin/settings";
import LanguageCard from "./LanguageCard";
import CustomerLogin from "../auth/CustomerLogin";
import CustomerRegister from "../auth/CustomerRegister";
import { isTokenValid, checkTokenUser } from "../../utils/authUtils";
import UserIcon from "../../images/user.png";

const logo = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/tlf-logo-cropped.png";
const logoMobile = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/tlf-logo-cropped-mobile.png";

// Within this distance from the top of the page the bar always stays visible.
const NAV_ALWAYS_VISIBLE_ABOVE_PX = 50;

const NavigationBar = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const booking = useSelector((state) => state.booking);

  // Source of truth for language
  const currentLng = i18n.language || "jp";

  const { cart } = booking;
  const [showNav, setShowNav] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const isAuthenticated = isTokenValid() && checkTokenUser();
  // Ensure the URL prefix is always lowercase even if i18n.language is uppercase
  const currentUrlLng = (i18n.language || "jp").toLowerCase();

  const localizedRoutes = ["/", "/work", "/book", "/fleet", "/pricing", "/services", "/fleet-types", "/cancellation-policy", "/maps"];

  const wellCabinPath = `/${currentUrlLng}/fleet-types?type=WELLCabinGranLuxe`;
  const wellCabinType = "WELLCabinGranLuxe";

  const getLocalizedPath = (path) => {
    const cleanPath = path === "" ? "/" : path;

    if (localizedRoutes.includes(cleanPath)) {
      // Force currentUrlLng to lowercase here
      return cleanPath === "/" ? `/${currentUrlLng}/` : `/${currentUrlLng}${cleanPath}`;
    }
    return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  };

  /**
   * Helper to determine if link is active by stripping language prefix from current URL
   */
  const isActive = (path) => {
    const pathParts = location.pathname.split("/");
    // If URL is /en/fleet, pathParts[1] is 'en', pathParts[2] is 'fleet'
    const currentPathWithoutLang = (pathParts[1] === "en" || pathParts[1] === "jp")
      ? `/${pathParts.slice(2).join("/")}`
      : location.pathname;

    const normalizedPath = currentPathWithoutLang === "" || currentPathWithoutLang === "//" ? "/" : currentPathWithoutLang;

    return normalizedPath === path;
  };

  const isWellCabinActive = () => {
    const pathParts = location.pathname.split("/");
    const currentPathWithoutLang = (pathParts[1] === "en" || pathParts[1] === "jp")
      ? `/${pathParts.slice(2).join("/")}`
      : location.pathname;
    const normalizedPath = currentPathWithoutLang === "" || currentPathWithoutLang === "//" ? "/" : currentPathWithoutLang;
    const searchParams = new URLSearchParams(location.search);

    return normalizedPath === "/fleet-types" && searchParams.get("type") === wellCabinType;
  };

  // Hide the bar while scrolling down, show it again when scrolling up or
  // near the top.
  //
  // This runs on every scroll event, so it is kept cheap: the last position
  // lives in a ref (not state), the work runs at most once per frame, and the
  // bar only re-renders when it actually flips between shown and hidden.
  // It used to store the position in state, which re-rendered the whole
  // navigation bar -- and re-attached this listener -- on every scroll event,
  // making the page stutter while scrolling.
  const lastScrollYRef = useRef(0);
  useEffect(() => {
    let frameRequestId = null;

    const updateNavVisibility = () => {
      frameRequestId = null;
      const currentScrollY = window.scrollY;
      const isScrollingUp = currentScrollY < lastScrollYRef.current;
      const isNearTop = currentScrollY < NAV_ALWAYS_VISIBLE_ABOVE_PX;
      lastScrollYRef.current = currentScrollY;
      // Same value -> React skips the re-render.
      setShowNav(isScrollingUp || isNearTop);
    };

    const handleScroll = () => {
      if (frameRequestId === null) {
        frameRequestId = window.requestAnimationFrame(updateNavVisibility);
      }
    };

    // passive: tells the browser we never block scrolling, so it can keep
    // scrolling smoothly without waiting for this handler.
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frameRequestId !== null) window.cancelAnimationFrame(frameRequestId);
    };
  }, []);

  // .navbar base bg is #121416;
  const navBgClass = "tw:!bg-[#121416]";

  // .nav-hidden => translateY(-100%)
  const navVisibilityClass = showNav ? "" : "tw:!-translate-y-full";

  // .nav-link (+ .nav-link-header / .active / hover) ported from custom.css
  const navLinkClass = (active) => `tw:!block tw:!text-center tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[500] tw:!no-underline tw:!px-4 tw:!py-2 tw:hover:!text-[#dbbe78] ${active ? "tw:!text-[#dbbe78] tw:!border-b-2 tw:!border-[#dbbe78]" : "tw:!text-[#f8f9fa]"}`;
  const navLinkClassMobile = (active) => `tw:!block tw:!text-left tw:!font-hanken-grotesk tw:!text-[15px] tw:!font-[500] tw:!no-underline tw:!px-2 tw:!py-2 tw:hover:!text-[#dbbe78] ${active ? "tw:!text-[#dbbe78] tw:!border-b-2 tw:!border-[#dbbe78]" : "tw:!text-[#f8f9fa]"}`;

  // Single source for the nav links so desktop & mobile stay in sync.
  const navItems = [
    { label: t("Home"), to: getLocalizedPath("/"), active: isActive("/") },
    { label: t("Book.Now"), to: getLocalizedPath("/book"), active: isActive("/book") },
    { label: t("Services"), to: getLocalizedPath("/services"), active: isActive("/services") },
    { label: t("WELL.Cabin.GranLuxe"), to: getLocalizedPath(wellCabinPath), active: isWellCabinActive() },
    { label: t("Fleet.Heading"), to: getLocalizedPath("/fleet"), active: isActive("/fleet") },
    { label: t("Pricing"), to: getLocalizedPath("/pricing"), active: isActive("/pricing") },
  ];

  // Auth button shared between desktop bar and mobile dropdown.
  const renderAuthButton = (extraClass = "") => {
    const inner = (
      <div className="tw:!flex tw:!flex-row tw:!justify-center tw:!items-center tw:!gap-[10px]">
        <img src={UserIcon} alt="user login icon" />
        <div className="tw:!font-hanken-grotesk tw:!text-white tw:!font-medium tw:!text-base">
          {isAuthenticated ? t("My Page") : t("Login")}
        </div>
      </div>
    );
    const className = `tw:!h-[45px] tw:!w-[135px] tw:!border tw:!border-[#7C7B7B] tw:!rounded-[5px] tw:!bg-transparent tw:!cursor-pointer ${extraClass}`;

    return isAuthenticated ? (
      <button
        type="button"
        onClick={() => {
          setMenuOpen(false);
          history.push("/my-page/bookings");
        }}
        className={className}
      >
        {inner}
      </button>
    ) : (
      <button
        type="button"
        className={className}
        data-bs-toggle="modal"
        data-bs-target="#login"
        onClick={() => setMenuOpen(false)}
      >
        {inner}
      </button>
    );
  };

  return (
    <>
    <nav
      className={`tw:!fixed tw:!top-0 tw:!right-0 tw:!left-0 tw:!z-[1030] tw:!h-[88px] tw:!flex tw:!items-center tw:!transition-transform tw:!duration-300 tw:!ease-out ${navBgClass} ${navVisibilityClass}`}
    >
      <div className="tw:!w-full tw:!flex tw:!items-center tw:!justify-between tw:!px-4 tw:md:!px-8">
        {/* Hamburger (mobile / tablet) */}
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          className="tw:lg:!hidden tw:!flex tw:!flex-col tw:!justify-center tw:!gap-[5px] tw:!bg-transparent tw:!border-0 tw:!p-2 tw:!cursor-pointer"
        >
          <span className={`tw:!block tw:!w-[25px] tw:!h-[3px] tw:!bg-[#dbbe78] tw:!origin-center tw:!transition-all tw:!duration-300 tw:!ease-out ${menuOpen ? "tw:!translate-y-[8px] tw:!rotate-45" : ""}`}></span>
          <span className={`tw:!block tw:!w-[25px] tw:!h-[3px] tw:!bg-[#dbbe78] tw:!transition-all tw:!duration-300 tw:!ease-out ${menuOpen ? "tw:!opacity-0" : ""}`}></span>
          <span className={`tw:!block tw:!w-[25px] tw:!h-[3px] tw:!bg-[#dbbe78] tw:!origin-center tw:!transition-all tw:!duration-300 tw:!ease-out ${menuOpen ? "tw:!-translate-y-[8px] tw:!-rotate-45" : ""}`}></span>
        </button>

        {/* Logo (mobile & desktop) */}
        <Link className="tw:!inline-block" to={getLocalizedPath("/")}>
          <img className="tw:!hidden tw:lg:!block tw:!h-[56px] tw:!w-auto tw:!max-w-full" src={logo} alt="logo" />
          <img className="tw:!block tw:lg:!hidden tw:!h-[40px] tw:!w-auto tw:!max-w-full" src={logoMobile} alt="logo" />
        </Link>

        {/* Cart & Notifications (mobile / tablet) */}
        <div className="tw:!flex tw:!items-center tw:!gap-3 tw:lg:!hidden">
          {isAuthenticated && (
            <div
              className="tw:!relative tw:!inline-block tw:!p-[3px] tw:!min-w-fit tw:!rounded-[2px] tw:!cursor-pointer tw:!text-white"
              onClick={() => history.push("/notifications")}
            >
              <span><i className="bi bi-bell-fill"></i></span>
              <span className="tw:!absolute tw:!-top-[3px] tw:!-right-[10px] tw:!w-[25px] tw:!h-[25px] tw:!rounded-full tw:!flex tw:!justify-center tw:!items-center tw:!text-[14px] tw:!font-hanken-grotesk tw:!font-semibold tw:!bg-[#ed553b] tw:!text-white">0</span>
            </div>
          )}
          <div
            className="tw:!relative tw:!inline-block tw:!p-[3px] tw:!min-w-fit tw:!rounded-[2px] tw:!cursor-pointer tw:!text-white"
            onClick={() => history.push("/Cart")}
          >
            <span><i className="bi bi-cart"></i></span>
            <span className="tw:!absolute tw:!-top-[3px] tw:!-right-[10px] tw:!w-[25px] tw:!h-[25px] tw:!rounded-full tw:!flex tw:!justify-center tw:!items-center tw:!text-[14px] tw:!font-hanken-grotesk tw:!font-semibold tw:!bg-white tw:!text-[#212529]">{cart?.length || "0"}</span>
          </div>
        </div>

        {/* Menu (desktop) */}
        <div className="tw:!hidden tw:lg:!flex tw:!items-center tw:gap-8">
          <ul className="tw:!flex tw:!flex-row tw:!items-center tw:!list-none tw:!pl-0 tw:!mb-0">
            {navItems.map((item) => (
              <li key={item.label} className="tw:!mx-1">
                <Link onClick={() => setMenuOpen(false)} className={`${navLinkClass(item.active)}`} to={item.to}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="tw:!flex tw:!items-center tw:!gap-3">
            <LanguageCard />

            {renderAuthButton()}

            {/* Cart Button */}
            <button
              type="button"
              className="tw:h-[45px] tw:!relative tw:!rounded tw:!font-hanken-grotesk tw:!font-bold tw:!text-base tw:!min-w-fit tw:!px-10 tw:!py-2 tw:!bg-[#D4B055] tw:!border tw:!border-[#dbbe78] tw:hover:!bg-[#eddfbb] tw:!cursor-pointer"
              onClick={() => history.push("/Cart")}
            >
              <div className="tw:!flex tw:!flex-row tw:!justify-center tw:!items-center">
                <div className="tw:!pr-2 tw:!text-base"><i className="bi bi-cart tw:!text-white"></i></div>
                <div className="tw:!text-base tw:!text-white tw:!font-hanken-grotesk tw:!font-medium">{t("Cart")}</div>
              </div>
              <span className="tw:!absolute tw:!top-0 tw:!left-full tw:!-translate-x-1/2 tw:!-translate-y-1/2">
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: 25, height: 25, borderRadius: "100%", fontSize: "12px", backgroundColor: "white" }}>
                  {cart?.length || "0"}
                </div>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Dropdown menu (mobile / tablet) */}
      <div
        className={`tw:!flex tw:lg:!hidden tw:!flex-col tw:!absolute tw:!top-full tw:!left-0 tw:!right-0 tw:!px-4 tw:!overflow-hidden tw:!transition-all tw:!duration-300 tw:!ease-out ${navBgClass} ${menuOpen ? "tw:!max-h-[80vh] tw:!opacity-100 tw:!pb-4 tw:!shadow-lg" : "tw:!max-h-0 tw:!opacity-0 tw:!pb-0 tw:!pointer-events-none"}`}
      >
        <ul className="tw:!flex tw:!flex-col tw:!list-none tw:!pl-0 tw:!mb-0 tw:!w-full">
          {navItems.map((item) => (
            <li key={item.label} className="tw:!w-full">
              <Link onClick={() => setMenuOpen(false)} className={navLinkClassMobile(item.active)} to={item.to}>
                {item.label}
              </Link>
            </li>
          ))}
          <div className="tw:!mt-2 tw:!flex tw:!flex-col tw:!items-start tw:mx-2">
            {renderAuthButton()}
          </div>
        </ul>
      </div>

    </nav>

    {/* Auth modals (rendered once, triggered from either menu). Kept OUTSIDE
        <nav> on purpose: the navbar slides itself up with a CSS transform when
        the page scrolls, and a fixed popup inside a transformed parent moves
        with it — the Log In popup then only showed at the top of the page. */}
    {!isAuthenticated && (
      <>
        <CustomerRegister useModal={true} modalId={"register"} />
        <CustomerLogin useModal={true} modalId={"login"} />
      </>
    )}
    </>
  );
};

export default NavigationBar;
